import { Helmet } from "react-helmet"
import {
    Box,
    Button,
    Container,
    Grid,
    Typography
} from "@mui/material"
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip"
import React, { useEffect, useMemo, useRef } from "react"
import { makeStyles } from "@mui/styles"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import cssVariables from "../../../../theme/cssVariables"
import * as Util from "../../../../services/global.service"

import WarningDialog from "../../../../components/WarningConfirm";
import SuccessDialog from "../../../../components/SimpleDialog";

import ReceiveForm from "../../../../components/emailNotificationCenter/ReceiveForm"
import StartDateForm from "../../../../components/emailNotificationCenter/StartDateForm"
import RecurringAndTimeScheduleForm from "../../../../components/emailNotificationCenter/RecurringAndTimeScheduleForm"
import PreviewEmailTemplate from "../../../../components/emailNotificationCenter/PreviewEmailTemplate"

import {
    getEmailNotificationDetail,
    putEmailNotificationDetail
} from "../../../../actions/admin/notification"

const useStyles = makeStyles({
    plusSvg: {
        "& > path": {
            fill: "#fff",
            stroke: "#fff",
        },
    },
    draggingListItem: {
        background: "rgb(235,235,235)",
    },
    toggleBtnRoot: {
        background: `transparent !important`,
        color: "#6B6B6B !important",
        width: "320px",
        border: "none !important",
    },
    toggleBtnSelected: {
        background: `${cssVariables.color.primary}!important`,
        color: "#fff!important",
    },
    flexPaper: {
        flex: 1,
        minWidth: 350,
        "box-shadow": "0px 2px 10px rgba(0, 0, 0, 0.1) !important",
        "border-radius": "10px !important        ",
    },
    tab: {
        fontSize: "16px !important",
        fontWeight: "300 !important",
        // color: "#A3A3A3 !important",
        "&.Mui-selected": {
            color: cssVariables.color.primary + " !important",
        },
    },
    root: {
        display: "flex",
        width: "100%",
        flexWrap: "wrap",
    },
})
const styles = {
    link: {
        color: "#5DA196",
        cursor: "pointer",
    },
    end: {
        color: "#A3A3A3",
    },
}

// custom hooks
// 1. useOnUpdate = NOT watch for state change on FIRST MOUNT
const useOnUpdate = (callback, deps) => {
    const isFirst = useRef(true);
    useEffect(() => {
        if (!isFirst.current) {
            callback()
        }
    }, deps)

    useEffect(() => {
        isFirst.current = false
    }, [])
}
// end custom hooks

const EmailNotificationDetail = () => {
    const classes = useStyles()
    const { t } = useTranslation()
    const { id } = useParams()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const notificationState = useSelector((state) => state.notificationCenter)
    const [saveButtonTrigger, setSaveButtonTrigger] = React.useState(false)
    const [features, setFeatures] = React.useState([])
    const [isDiscardDialogOpen, setIsDiscardDialogOpen] = React.useState(false)
    const [isSuccessEditDialogOpen, setIsSuccessEditDialogOpen] = React.useState(false)
    const [isPreviewOpen, setIsPreviewOpen] = React.useState(false)
    const [previewEmailType, setPreviewEmailType] = React.useState("")
    const [previewEmailBanner, setPreviewEmailBanner] = React.useState("")

    const handleCancel = () => {
        setIsDiscardDialogOpen(true)
    }

    const handleConfirmDiscard = () => {
        navigate("/admin/email-notification-center")
    }

    const handleSuccessEdit = () => {
        navigate("/admin/email-notification-center")
    }

    const validateForm = () => {
        console.log("in validate form")
        setSaveButtonTrigger(!saveButtonTrigger)
        dispatch({
            type: "SET_DO_VALIDATE_EMAIL_NOTIFICATION_FORM",
            payload: true
        })
    }

    const handleOpenPreviewEmail = (val) => {
        setPreviewEmailType(val)
        setIsPreviewOpen(true)
    }

    const getTimeOnly = (date) => {
        let hour = date.getHours()
        let min = date.getMinutes()
        return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}:00`
    }

    const handleSubmit = () => {
        console.log("submit")

        let putData = []
        const checkValidSection = () => {
            let isValid = true
            let validStatus = {}
            features.forEach(feature => {
                if (feature === "receive") validStatus["receive"] = notificationState.isReceiveFormValid
                if (feature === "start_date") validStatus["start_date"] = notificationState.isStartDateFormValid
                if (feature === "until_end_date") validStatus["until_end_date"] = notificationState.isEndDateFormValid
                if (feature === "overdue") validStatus["overdue"] = notificationState.isOverdueFormValid
            })
            for (const [_, value] of Object.entries(validStatus)) {
                if (!value) isValid = false
            }
            return isValid
        }
        if (checkValidSection()) {
            // receive form
            if (features.includes("receive")) putData.push(notificationState.receiveForm)

            if (features.includes("start_date")) {
                // start date form
                let startDateForm = {
                    ...notificationState.startDateForm,
                    reminder: {
                        time: getTimeOnly(notificationState.startDateForm.reminder.time)
                    }
                }
                putData.push(startDateForm)
            }
            if (features.includes("until_end_date")) {
                // end date form
                let endDateTime = [...notificationState.endDateForm.reminder.schedule_time]
                if (notificationState.endDateForm.reminder.type === "recurring") {
                    endDateTime = endDateTime.map((item, index) => {
                        return {
                            days: item.days,
                            time: getTimeOnly(item.time)
                        }
                    })
                } else {
                    endDateTime = endDateTime.map((item, index) => {
                        return {
                            seq: `${index + 1}`,
                            days: item.days,
                            time: getTimeOnly(item.time)
                        }
                    })
                }

                let endDateForm = {
                    ...notificationState.endDateForm,
                    reminder: {
                        ...notificationState.endDateForm.reminder,
                        schedule_time: endDateTime
                    }
                }
                putData.push(endDateForm)
            }
            if (features.includes("overdue")) {
                // overdue form
                let overdueTime = [...notificationState.overdueForm.reminder.schedule_time]
                if (notificationState.overdueForm.reminder.type === "recurring") {
                    overdueTime = overdueTime.map((item, index) => {
                        return {
                            days: item.days,
                            time: getTimeOnly(item.time)
                        }
                    })
                } else {
                    overdueTime = overdueTime.map((item, index) => {
                        return {
                            seq: `${index + 1}`,
                            days: item.days,
                            time: getTimeOnly(item.time)
                        }
                    })
                }
                let overdueForm = {
                    ...notificationState.overdueForm,
                    reminder: {
                        ...notificationState.overdueForm.reminder,
                        schedule_time: overdueTime
                    }
                }
                putData.push(overdueForm)
            }

            // call API
            dispatch(putEmailNotificationDetail(id, putData)).then(resp => {
                setIsSuccessEditDialogOpen(true)
            })
        }
    }

    // useEffect
    useEffect(() => {
        dispatch(getEmailNotificationDetail(id)).then(resp => {
            dispatch({
                type: "SET_EMAIL_NOTIFICATION_PAGE_CODE",
                payload: resp?.name ?? "-"
            })
            setPreviewEmailBanner(resp?.banner ?? "")

            resp = resp.list
            dispatch({
                type: "SET_EMAIL_NOTIFICATION_LIST",
                payload: resp
            })
            let tmpFeatures = []
            resp.forEach((item, _) => {
                if (item?.feature) {
                    tmpFeatures.push(item.feature)
                }
            })
            setFeatures(tmpFeatures)
        })
        // clean up state
        return () => {
            dispatch({
                type: "RESET_NOTIFICATION_TO_INITIAL_STATE"
            })
        }
    }, [])

    useOnUpdate(() => {
        if (saveButtonTrigger !== undefined && saveButtonTrigger !== null) handleSubmit()
    }, [saveButtonTrigger])
    // end useEffect

    return (
        <Box
            sx={{ maxHeight: "100%", overflowY: "auto" }}
        >
            <Helmet>
                <title>Email Notification Center - Edit | PMS </title>
            </Helmet>
            <Box
                sx={{
                    backgroundColor: "background.default",
                    minHeight: "100%",
                    py: 3,
                }}
            >
                <Container maxWidth="xl">
                    <Grid container mt={1} mb={1}>
                        <Typography fontSize={16} lineHeight={1} color="#68717A">
                            <span style={styles.link} onClick={() => navigate('/admin/email-notification-center')}>
                                {` ${t('emailNotificationCenter.label.title')}`}
                            </span>
                            {` / `}
                            <span style={styles.end}>{t("emailNotificationCenter.label.edit")}</span>
                        </Typography>
                    </Grid>
                    <Grid container mb={2}>
                        <Grid item display="flex" alignItems="center">
                            <Typography
                                color="#404349"
                                fontSize={36}
                                fontWeight="600"
                            >
                                {`${t("emailNotificationCenter.label.edit")} (${notificationState.pageCode !== "" ? Util.capitalizedString(notificationState.pageCode) : "-"})`}
                            </Typography>
                        </Grid>
                    </Grid>
                    {
                        features.includes("receive") &&
                        <Grid container mb="30px">
                            <Grid item xs={12}>
                                <ReceiveForm saveButtonClicked={saveButtonTrigger} doPreview={handleOpenPreviewEmail} data={features["receive"]} />
                            </Grid>
                        </Grid>
                    }
                    {
                        features.includes("start_date") &&
                        <Grid container mb="30px">
                            <Grid item xs={12}>
                                <StartDateForm saveButtonClicked={saveButtonTrigger} doPreview={handleOpenPreviewEmail} />
                            </Grid>
                        </Grid>
                    }
                    {
                        features.includes("until_end_date") &&
                        <Grid container mb="30px">
                            <Grid item xs={12}>
                                <RecurringAndTimeScheduleForm type="until_end_date" saveButtonClicked={saveButtonTrigger} doPreview={handleOpenPreviewEmail} />
                            </Grid>
                        </Grid>
                    }
                    {
                        features.includes("overdue") &&
                        <Grid container mb="30px">
                            <Grid item xs={12}>
                                <RecurringAndTimeScheduleForm type="overdue" saveButtonClicked={saveButtonTrigger} doPreview={handleOpenPreviewEmail} />
                            </Grid>
                        </Grid>
                    }
                    <Grid container mt={2}>
                        <Grid item display="flex" alignItems="center" ml="auto">
                            <Button onClick={handleCancel} color="black" variant="outlined" sx={{ width: "114px", mr: 2 }}>
                                <Typography fontWeight="500">{t("label.cancel")}</Typography>
                            </Button>
                            <Button
                                size="small"
                                aria-controls="save"
                                aria-haspopup="false"
                                onClick={() => { validateForm() }}
                                color="primary"
                                variant="contained"
                                sx={{ textTransform: 'none', width: "114px" }}
                            >
                                <Typography>{t('label.save')}</Typography>
                            </Button>
                        </Grid>
                    </Grid>
                </Container>
                <WarningDialog open={isDiscardDialogOpen} doConfirm={handleConfirmDiscard} setValue={setIsDiscardDialogOpen} title={t("emailNotificationCenter.label.dialog.discardTitle")} text={t("emailNotificationCenter.label.dialog.discardText")} />
                <SuccessDialog open={isSuccessEditDialogOpen} setValue={handleSuccessEdit} text={t("emailNotificationCenter.label.dialog.saveSuccess")} title={t("label.success")} />
                {/* preview email template */}
                <PreviewEmailTemplate open={isPreviewOpen} setValue={setIsPreviewOpen} type={previewEmailType} banner={previewEmailBanner} />
            </Box>
        </Box>
    )
}
export default EmailNotificationDetail
